import { IEntity } from '../inputs.datastore/types';
import { assign } from 'lodash';

export enum AGENT_MODEL {
  VTA = 'vTA',
  VTA_TS = 'vTA-TS',
  VTA_LB = 'vTA-LB',
  VPMA = 'vPMA',
  VPMA_L = 'vPMA-L',
  VMA = 'vMA',
  VQT = 'vQT',
  QT600 = 'QT600-10',
  JMEP_1G = 'JMEP-1G',
  JMEP_10G = 'JMEP-10G',
  NSC = 'NSC',
  MTS = 'MTS',
  MTS_5800V2 = 'MTS-5800v2',
  TB_MTS_5800_100G = 'TB/MTS-5800-100G',
  TB_MTS_5800v2 = 'TB/MTS-5800v2',
  MTS_5882 = 'MTS-5882',
  SC4800 = 'SC4800',
  SC4800P = 'SC4800P',
  TB_5800v2 = 'TB-5800v2',
  TB_5882 = 'TB-5882',
  TB_5800_100G = 'TB-5800-100G',
  MTS_5800_100G = 'MTS-5800-100G',
  MAP2100 = 'MAP-2100',
  UNMANAGED = 'Unmanaged',
  ESAM = 'ESAM',
  WINDOWS = 'Windows',
  VIRTUAL = 'Virtual',
  HARDWARE = 'Hardware',
  MAC = 'Mac',
  LINUX = 'Linux',
  VSPA = 'vSPA',
  VSPA_SAT_LB = 'vSPA-SAT-LB',
  VSPA_SAT = 'vSPA-SAT',
  VSPA_SAT_TS = 'vSPA-SAT-TS',
  VSPA_PM = 'vSPA-PM',
  VSPA_PM_L = 'vSPA-PM-L'
}
export class Device implements IEntity {
  isDeleted: boolean;
  escapedName;
  queueStatus?;
  dataInfo?;
  deviceData = {} as IDeviceData;
  interfaces: Array<Device> = [];

  constructor(data: IDeviceData) {
    this.escapedName = this.escapeString(data.name);
    this.setDevice(data);
    this.isDeleted = this.deviceData?.deleted ? this.deviceData.deleted : false;
  }

  setDevice(data: IDeviceData): void {
    assign(this.deviceData, data);
  }

  getDevice(): IDeviceData {
    return this.deviceData;
  }

  getId(): string {
    return this.deviceData.id;
  }

  getStatus(): string {
    return this.deviceData.status;
  }

  getParentId(): string {
    return this.deviceData.parentid;
  }

  setInterfaces(interfaces = []): void {
    this.interfaces = interfaces;
  }

  getInterface(ifIndex: number): Device {
    return this.interfaces.find(i => i.getParamByName('ifIndex')?.value === String(ifIndex));
  }

  getName(): string {
    return this.deviceData.name;
  }

  getDeviceName(): string {
    return this.deviceData.deviceName;
  }

  getUniqueName(): string {
    return this.deviceData.nameId;
  }

  getUniqueId(): string {
    return this.deviceData.uniqueId;
  }

  getModel(): string {
    return this.getParamByName('model')?.value || null;
  }

  getVersion(): { name: string; value: string } {
    return this.getParamByName('version');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getParams(): any[] {
    return this.deviceData.params || [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getParamByName(name: string): any {
    return this.getParams().find(param => param.name === name);
  }

  getEscapedName(): string {
    return this.escapedName;
  }

  escapeString(input: string): string {
    return JSON.stringify(input).replace(/^"(.*)"$/, '$1');
  }

  getIpAddress(): string {
    return this.deviceData.ipaddress;
  }

  getMgmtIp(): string {
    return this.deviceData.mgmtIPaddress;
  }

  getType(): DeviceType {
    return this.deviceData.type;
  }

  setStatus(status: string): void {
    this.deviceData.status = status;
  }

  getDeviceType(): string {
    return this.deviceData.model.model || null;
  }

  getVendor(): string {
    return this.deviceData.model.vendor || null;
  }

  getPhyId(): string {
    return this.deviceData.phyId || '';
  }

  getPhyName(): string {
    return this.deviceData.phyName || null;
  }
}

export interface IDeviceData {
  id: string;
  alias: string;
  ipaddress: string;
  relectorIp: string;
  mgmtIPaddress: string;
  uniqueId: string;
  name: string;
  nameId: string;
  phyId: string;
  phyName: string;
  params: Array<{ name: string; value: string }>;
  parentid: string;
  parenttype: DeviceType;
  site: number;
  location?: string;
  lat?: number;
  lon?: number;
  status: string;
  type: DeviceType;
  tenantId: string;
  agentType: string;
  state: string;
  substatus: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any;
  lastUpdateStartTime: string;
  lastUpgradeMessageTime;
  lastUpgradeMessage: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  capabilities: any;
  iconName?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  connections?: any;
  deleted?: boolean;
  deviceName?: string;
  comMode?: string;
  activity?: {
    status: string;
    msg: string;
    tests: any;
  };
}

export type DeviceType =
  | 'GigaStor'
  | 'GigaStorInstance'
  | 'GigaFlowServer'
  | 'GigaFlowRouter'
  | 'GigaTestAgent'
  | 'GigaFlowRouterInterface'
  | 'unmanaged';
